// @flow @format
import Client from 'lib/client';

export type Metafile = {
	Created: string,
	Dirty: 0 | 1,
	Hash: string,
	Id: string,
	Name: string,
	Parent: string,
	Repository: string,
	Size: number,
	Type: 0 | 1,
	VersionFin: number,
	VersionIni: number,
};

export type File = {
	created: string,
	deleted: false,
	id: string,
	name: string,
	num_versions: number,
	repo: string,
	size: number,
	type: 0 | 1,
};

export type FileNode = {
	breadcrumb: {id: string, name: string}[],
	files: File[],
	repository: {id: string, name: string},
	machine: {id: string, name: string},
};

export type FileRequest = {
	owner: string,
	machine_id: string,
	metafile_id: string,
	repository_id: string,
	hash: string,
	status: string,
	num_blocks: number,
	LastAccess: string,
};

class Api {
	static PORT = ':5108';

	_client: Client;

	constructor(server: string, token: string) {
		const baseUrl = server + Api.PORT;

		this._client = new Client(
			baseUrl,
			new Headers({
				cloner_key: token,
				Accept: 'application/json, text/plain,/',
				Connection: 'keep-alive',
				Referer: 'https://login.cloner.cl',
				'Accept-Language': 'es-ES,es;q=0.8',
				'Accept-Encoding': 'gzip, deflate, sdch, br',
			})
		);
	}

	getFiles(
		id: string,
		repoId: string,
		version: number = 99999999
	): Promise<FileNode> {
		return this._client
			.get(`/files?id=${id}&repoid=${repoId}&version=${version}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json();
				}
				return Promise.reject(
					`blocks-api: could not fetch children of file "${id}"`
				);
			});
	}

	getMetafile(id: string, repoId: string): Promise<Metafile> {
		return this._client
			.get(`/metafile?id=${id}&repo_id=${repoId}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json();
				}
				return Promise.reject(
					`blocks-api: could not fetch metafile for file "${id}"`
				);
			});
	}

	setupFile(repoId: string, metafileId: string): Promise<Response> {
		const body = JSON.stringify({
			repository_id: repoId,
			metafile_id: metafileId,
		});
		return this._client.post('/file/setup', 'application/json', body);
	}

	queryFile(repoId: string, metafileId: string): Promise<FileRequest> {
		return this._client
			.get(`/file/query?repo_id=${repoId}&metafile_id=${metafileId}`)
			.then(resp => {
				if (resp.ok) {
					return resp.json();
				}
				return Promise.reject(
					`blocks-api: ${metafileId} setup failed or is not ready`
				);
			});
	}

	queryFolder(repoId: string, metafileId: string): Promise<Response> {
		return this._client.get(
			`/folder/query?repo_id=${repoId}&metafile_id=${metafileId}`
		);
	}

	retrieveFile(
		repoId: string,
		metafileId: string,
		position: number
	): Promise<Blob> {
		return this._client
			.get(
				`/file/retrieve?repo_id=${repoId}&metafile_id=${metafileId}&pos=${position}`
			)
			.then(resp => {
				if (resp.ok) {
					return resp.blob();
				}
				return Promise.reject(
					`blocks-api: could not fetch block at position "${position}" of "${metafileId}"`
				);
			});
	}

	setupFolder(
		repoId: string,
		metafileId: string,
		includeDeleted: boolean
	): Promise<Response> {
		const body = JSON.stringify({
			repository_id: repoId,
			metafile_id: metafileId,
			deleted: includeDeleted,
		});
		return this._client
			.post('/folder/setup', 'application/json', body)
			.then(resp => {
				if (resp.ok) {
					return resp;
				}

				return Promise.reject(resp);
			});
	}

	ackFolder(n: number): Promise<Response> {
		const body = JSON.stringify({n: n});
		return this._client.post(
			'/folder/acknowledge',
			'application/json',
			body
		);
	}

	cancelFolder(): Promise<Response> {
		return this._client.post('/folder/cancel', '', '');
	}

	retrieveFolder(
		repoId: string,
		metafileId: string
	): Promise<{status: number, blob?: Blob, size?: number}> {
		return this._client
			.get(`/folder/retrieve?repo_id=${repoId}&metafile_id=${metafileId}`)
			.then(resp => {
				switch (resp.status) {
					case 200:
						return {
							status: 200,
						};
					case 202:
						return resp.blob().then(blob => ({
							status: 202,
							blob: blob,
							size: parseInt(resp.headers.get('x-data-size'), 10),
						}));
					case 304:
						return {
							status: 304,
						};
					default:
						return Promise.reject(
							`blocks-api: could not fetch zip chunk of "${metafileId}"`
						);
				}
			});
	}
}

export default Api;



// WEBPACK FOOTER //
// ./src/lib/blocks-api/index.js